import { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { formatApiError } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { UserPlus, Loader2, Calendar, Users, BookOpen, CreditCard, Search, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminEnrollments() {
  const [enrollments, setEnrollments] = useState([]);
  const [students, setStudents] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [searchStudent, setSearchStudent] = useState('');
  const [form, setForm] = useState({ student_id: '', course_id: '', subscription_type: 'monthly', notes: '' });
  const [lastResult, setLastResult] = useState(null);

  const fetchData = async () => {
    try {
      const [enrollRes, studentsRes, modulesRes] = await Promise.all([
        axios.get(`${API}/admin/enrollments`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/admin/students`, { headers: getAuthHeaders(), withCredentials: true }),
        axios.get(`${API}/courses`, { headers: getAuthHeaders(), withCredentials: true })
      ]);
      setEnrollments(enrollRes.data);
      setStudents(studentsRes.data);
      setModules(modulesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const selectedModule = modules.find(m => m.id === form.course_id);
  const displayPrice = selectedModule
    ? (form.subscription_type === 'annual' ? selectedModule.price_annual : selectedModule.price_monthly)
    : 0;

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchStudent.toLowerCase()) ||
    s.email.toLowerCase().includes(searchStudent.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.student_id || !form.course_id) return;
    setError(''); setSubmitting(true); setLastResult(null);
    try {
      const res = await axios.post(`${API}/admin/enrollments`, form, { headers: getAuthHeaders(), withCredentials: true });
      setLastResult(res.data);
      toast.success('Inscripción registrada exitosamente');
      setForm({ student_id: '', course_id: '', subscription_type: 'monthly', notes: '' });
      setSearchStudent('');
      fetchData();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Layout><div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="admin-enrollments-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">Inscripciones Manuales</h1>
            <p className="text-[#737373] mt-2">Inscribe estudiantes que pagaron fuera de la plataforma</p>
          </div>
          <Button data-testid="new-enrollment-btn" onClick={() => { setShowDialog(true); setLastResult(null); setError(''); }} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
            <UserPlus className="h-4 w-4 mr-2" /> Nueva Inscripción
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Inscripciones Activas</p><p className="text-3xl font-bold text-[#1A1A1A]">{enrollments.length}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center"><Users className="h-6 w-6 text-[#2D6A4F]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Pagos Externos</p><p className="text-3xl font-bold text-[#1A1A1A]">{enrollments.filter(e => e.payment_method === 'external').length}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#B8860B]/10 flex items-center justify-center"><CreditCard className="h-6 w-6 text-[#B8860B]" /></div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div><p className="text-sm text-[#737373]">Estudiantes Registrados</p><p className="text-3xl font-bold text-[#1A1A1A]">{students.length}</p></div>
                <div className="h-12 w-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center"><BookOpen className="h-6 w-6 text-[#8B1538]" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enrollments Table */}
        <Card className="bg-white border border-[#E5DFD3] shadow-sm">
          <CardHeader><CardTitle className="font-heading text-xl text-[#1A1A1A]">Inscripciones Activas</CardTitle></CardHeader>
          <CardContent>
            {enrollments.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="enrollments-table">
                  <thead>
                    <tr className="border-b border-[#E5DFD3] text-left">
                      <th className="pb-3 font-semibold text-[#737373]">Estudiante</th>
                      <th className="pb-3 font-semibold text-[#737373]">Módulo</th>
                      <th className="pb-3 font-semibold text-[#737373]">Plan</th>
                      <th className="pb-3 font-semibold text-[#737373]">Método</th>
                      <th className="pb-3 font-semibold text-[#737373]">Vence</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E5DFD3]">
                    {enrollments.map((e) => (
                      <tr key={e.id} data-testid={`enrollment-row-${e.id}`} className="hover:bg-[#F5F2EA]">
                        <td className="py-3">
                          <div><p className="font-medium text-[#1A1A1A]">{e.student_name}</p><p className="text-xs text-[#737373]">{e.student_email}</p></div>
                        </td>
                        <td className="py-3 text-[#4A4A4A]">{e.module_title}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.subscription_type === 'annual' ? 'bg-[#B8860B]/10 text-[#B8860B]' : 'bg-[#8B1538]/10 text-[#8B1538]'}`}>
                            {e.subscription_type === 'annual' ? 'Anual' : 'Mensual'}
                          </span>
                        </td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.payment_method === 'external' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#737373]/10 text-[#737373]'}`}>
                            {e.payment_method === 'external' ? 'Pago Externo' : 'Plataforma'}
                          </span>
                        </td>
                        <td className="py-3 text-[#737373]">{new Date(e.expires_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12"><Users className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" /><p className="text-[#737373]">No hay inscripciones activas</p></div>
            )}
          </CardContent>
        </Card>

        {/* New Enrollment Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Inscripción Manual</DialogTitle>
              <DialogDescription>Inscribe a un estudiante que realizó su pago fuera de la plataforma</DialogDescription>
            </DialogHeader>

            {lastResult && (
              <Alert className="bg-[#2D6A4F]/10 border-[#2D6A4F]/20">
                <CheckCircle2 className="h-4 w-4 text-[#2D6A4F]" />
                <AlertDescription className="text-[#2D6A4F]">
                  <strong>{lastResult.student_name}</strong> inscrito en <strong>{lastResult.module_title}</strong>.
                  Vence: {new Date(lastResult.expires_at).toLocaleDateString('es-ES')}.
                  Monto: ${lastResult.amount?.toLocaleString()}
                </AlertDescription>
              </Alert>
            )}

            {error && <Alert variant="destructive" className="bg-[#9B2226]/10 border-[#9B2226]/20 text-[#9B2226]"><AlertDescription>{error}</AlertDescription></Alert>}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Student selection */}
              <div className="space-y-2">
                <Label>Estudiante</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#737373]" />
                  <Input
                    data-testid="search-student-input"
                    placeholder="Buscar por nombre o email..."
                    value={searchStudent}
                    onChange={(e) => setSearchStudent(e.target.value)}
                    className="pl-10 border-[#E5DFD3]"
                  />
                </div>
                <div className="border border-[#E5DFD3] rounded-md max-h-40 overflow-y-auto">
                  {filteredStudents.length > 0 ? filteredStudents.slice(0, 20).map((s) => (
                    <button
                      type="button" key={s.id}
                      data-testid={`student-option-${s.id}`}
                      onClick={() => { setForm({...form, student_id: s.id}); setSearchStudent(s.name); }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm border-b border-[#E5DFD3] last:border-b-0 transition-colors ${
                        form.student_id === s.id ? 'bg-[#8B1538]/10 text-[#8B1538]' : 'hover:bg-[#F5F2EA] text-[#4A4A4A]'
                      }`}
                    >
                      <div><span className="font-medium">{s.name}</span><span className="text-xs text-[#737373] ml-2">{s.email}</span></div>
                      {form.student_id === s.id && <CheckCircle2 className="h-4 w-4 text-[#8B1538]" />}
                    </button>
                  )) : (
                    <p className="px-3 py-2 text-sm text-[#737373] italic">No se encontraron estudiantes</p>
                  )}
                </div>
              </div>

              {/* Module selection */}
              <div className="space-y-2">
                <Label>Módulo</Label>
                <Select value={form.course_id} onValueChange={(v) => setForm({...form, course_id: v})}>
                  <SelectTrigger data-testid="module-select" className="border-[#E5DFD3]"><SelectValue placeholder="Selecciona el módulo" /></SelectTrigger>
                  <SelectContent>
                    {modules.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.title} — ${m.price_monthly?.toLocaleString()}/mes</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subscription type */}
              <div className="space-y-2">
                <Label>Tipo de Pago</Label>
                <RadioGroup value={form.subscription_type} onValueChange={(v) => setForm({...form, subscription_type: v})} className="grid grid-cols-2 gap-3">
                  <div className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer ${form.subscription_type === 'monthly' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3]'}`}>
                    <RadioGroupItem value="monthly" id="enroll-m" />
                    <Label htmlFor="enroll-m" className="cursor-pointer flex-1">
                      <span className="font-medium text-[#1A1A1A] block">Mensual</span>
                      <span className="text-xs text-[#737373]">30 días</span>
                    </Label>
                  </div>
                  <div className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer ${form.subscription_type === 'annual' ? 'border-[#8B1538] bg-[#8B1538]/5' : 'border-[#E5DFD3]'}`}>
                    <RadioGroupItem value="annual" id="enroll-a" />
                    <Label htmlFor="enroll-a" className="cursor-pointer flex-1">
                      <span className="font-medium text-[#1A1A1A] block">Anual</span>
                      <span className="text-xs text-[#737373]">365 días</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Summary */}
              {selectedModule && form.student_id && (
                <div className="bg-[#F5F2EA] rounded-md p-4 space-y-1">
                  <p className="text-sm font-semibold text-[#1A1A1A]">Resumen</p>
                  <p className="text-sm text-[#4A4A4A]">Módulo: <span className="font-medium">{selectedModule.title}</span></p>
                  <p className="text-sm text-[#4A4A4A]">Monto: <span className="font-bold text-[#8B1538]">${displayPrice.toLocaleString()}</span></p>
                  <p className="text-sm text-[#4A4A4A]">Vigencia: <span className="font-medium">{form.subscription_type === 'annual' ? '365 días' : '30 días'}</span></p>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notas (opcional)</Label>
                <Textarea
                  data-testid="enrollment-notes"
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  placeholder="Ej: Transferencia bancaria, comprobante #123..."
                  rows={2} className="border-[#E5DFD3]"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-[#E5DFD3]">Cancelar</Button>
                <Button type="submit" disabled={submitting || !form.student_id || !form.course_id} className="bg-[#8B1538] hover:bg-[#70102B] text-white">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Registrar Inscripción'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
